import type { JSX } from 'react';

import { Amount, fill, kindLabel } from './components';
import { useMyCredits, useMyMembership, useMyWallet } from './queries';
import { rupees } from './schemas';
import { strings } from './strings';

/**
 * §6 client `/me/wallet` — the balance, what expires when, the history.
 *
 * **It says when the money dies, on the screen rather than in a reminder.**
 * A prepaid balance with a silent expiry date is the part of this product
 * most likely to make somebody angry; the reminder is a courtesy and this
 * is the record.
 *
 * Every row shows its sign (`components.Amount`): `−₹500` and `₹500` are
 * different events in somebody's financial history, and a statement printed
 * in black and white has to tell them apart.
 */
export function MyWalletPage(): JSX.Element {
  const walletQuery = useMyWallet();
  const credits = useMyCredits();
  const membership = useMyMembership();

  // Hoisted for the reason `ClientPrepaidPanel` gives: `?? []` written twice
  // leaves a branch nothing can reach.
  const creditRows = credits.data ?? [];
  const activeMembership = membership.data ?? null;
  const empty =
    walletQuery.data?.balance_paise === 0 && credits.data?.length === 0 && membership.data === null;

  return (
    <section className="space-y-4 p-4" data-testid="my-wallet">
      <h1 className="text-xl font-semibold">{strings.myWalletTitle}</h1>

      {walletQuery.isPending ? <p>{strings.loading}</p> : null}
      {walletQuery.isError ? (
        <p role="alert" data-testid="my-wallet-failed">
          {strings.failed}
        </p>
      ) : null}
      {empty ? <p data-testid="my-wallet-empty">{strings.myNothing}</p> : null}

      {/* BP-36 rule 5: what the client holds beside what has happened to it, from 768 px. */}
      <div
        data-testid="my-wallet-columns"
        className="space-y-4 @3xl:grid @3xl:grid-cols-2 @3xl:items-start @3xl:gap-8 @3xl:space-y-0"
      >
        <div className="space-y-4">
          {walletQuery.data === undefined ? null : (
            <div>
              <p className="text-lg">
                <Amount paise={walletQuery.data.balance_paise} />
              </p>
              {walletQuery.data.expiring_soon_paise > 0 ? (
                <p data-testid="my-expiring">
                  {fill(strings.expiringOn, {
                    amount: rupees(walletQuery.data.expiring_soon_paise),
                    when: walletQuery.data.expiring_on,
                  })}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="my-nothing-expiring">
                  {strings.nothingExpiring}
                </p>
              )}
            </div>
          )}

          {creditRows.length > 0 ? (
            <div>
              <h2 className="font-medium">{strings.myCreditsTitle}</h2>
              <ul className="space-y-1">
                {creditRows.map((credit) => (
                  <li key={credit.credit_id} className="text-sm" data-testid="my-credit-row">
                    {fill(strings.creditLeft, {
                      left: credit.qty_remaining,
                      total: credit.qty_total,
                      when: credit.expires_at,
                    })}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {activeMembership === null ? null : (
            <div data-testid="my-membership">
              <h2 className="font-medium">{strings.myMembershipTitle}</h2>
              <p className="text-sm">
                {fill(strings.membershipRuns, {
                  from: activeMembership.starts_on,
                  to: activeMembership.ends_on,
                })}
              </p>
            </div>
          )}
        </div>

        <div>
          <h2 className="font-medium">{strings.historyTitle}</h2>
          <ul className="space-y-1" data-testid="my-history">
            {(walletQuery.data?.history ?? []).map((row) => (
              <li key={row.id} className="flex items-center gap-3 text-sm" data-testid="history-row">
                <span>{row.created_at.slice(0, 10)}</span>
                <span>{kindLabel(row.kind)}</span>
                <Amount paise={row.amount_paise} />
                {row.reason === null ? null : (
                  <span className="text-muted-foreground">{row.reason}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
